import React, { useState, useEffect } from "react";
import { 
    Card, Table, Row, Col, Button, Spinner, Alert, Badge,
    Form, Modal, InputGroup, OverlayTrigger, Tooltip, Pagination
} from "react-bootstrap";
import { Link, useNavigate } from "react-router-dom";
import { formatDate } from "../utils/formatters";
import Footer from "../layouts/Footer";
import Header from "../layouts/Header";

export default function Utilisateurs() {
    const navigate = useNavigate();
    const [utilisateurs, setUtilisateurs] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [showModal, setShowModal] = useState(false);
    const [modalMode, setModalMode] = useState('add');
    const [currentUser, setCurrentUser] = useState(null);
    const [formData, setFormData] = useState({
        nom_utilisateur: '',
        prenom_utilisateur: '',
        email: '',
        role: 'commercial',
        mot_de_passe: '',
        actif: true
    });
    const [saving, setSaving] = useState(false);
    const [success, setSuccess] = useState('');
    const [formError, setFormError] = useState('');

    // États pour la pagination
    const [currentPage, setCurrentPage] = useState(1);
    const [itemsPerPage] = useState(10);
    
    // États pour le modal de suppression
    const [showDeleteModal, setShowDeleteModal] = useState(false);
    const [userToDelete, setUserToDelete] = useState(null);
    const [isDeleting, setIsDeleting] = useState(false);

    useEffect(() => {
        fetchUtilisateurs();
    }, []);

    const fetchUtilisateurs = async () => {
        try {
            setLoading(true);
            setError(null);
            
            const token = localStorage.getItem("token");
            const response = await fetch(
                'https://sawd.julesferry-ga.com/api/parametres.php?action=utilisateurs',
                { headers: { 'Authorization': `Bearer ${token}` } }
            );
            
            if (!response.ok) {
                const errorData = await response.json();
                throw new Error(errorData.message || "Erreur chargement");
            }
            
            const result = await response.json();
            setUtilisateurs(result.data || []);
            setCurrentPage(1);
        } catch (err) {
            setError(err.message);
        } finally {
            setLoading(false);
        }
    };

    // Pagination
    const indexOfLastItem = currentPage * itemsPerPage;
    const indexOfFirstItem = indexOfLastItem - itemsPerPage;
    const currentItems = utilisateurs.slice(indexOfFirstItem, indexOfLastItem);
    const totalPages = Math.ceil(utilisateurs.length / itemsPerPage);

    const handlePageChange = (pageNumber) => {
        setCurrentPage(pageNumber);
        window.scrollTo({ top: 0, behavior: 'smooth' });
    };

    const renderPagination = () => {
        let items = [];
        const maxVisible = 5;
        let startPage = Math.max(1, currentPage - Math.floor(maxVisible / 2));
        let endPage = Math.min(totalPages, startPage + maxVisible - 1);

        if (endPage - startPage + 1 < maxVisible) {
            startPage = Math.max(1, endPage - maxVisible + 1);
        }

        if (startPage > 1) {
            items.push(
                <Pagination.Item key={1} onClick={() => handlePageChange(1)}>
                    1
                </Pagination.Item>
            );
            if (startPage > 2) {
                items.push(<Pagination.Ellipsis key="ellipsis-start" disabled />);
            }
        }

        for (let number = startPage; number <= endPage; number++) {
            items.push(
                <Pagination.Item 
                    key={number} 
                    active={number === currentPage}
                    onClick={() => handlePageChange(number)}
                >
                    {number}
                </Pagination.Item>
            );
        }

        if (endPage < totalPages) {
            if (endPage < totalPages - 1) {
                items.push(<Pagination.Ellipsis key="ellipsis-end" disabled />);
            }
            items.push(
                <Pagination.Item key={totalPages} onClick={() => handlePageChange(totalPages)}>
                    {totalPages}
                </Pagination.Item>
            );
        }

        return items;
    };

    const handleAdd = () => {
        setModalMode('add');
        setCurrentUser(null);
        setFormData({
            nom_utilisateur: '',
            prenom_utilisateur: '',
            email: '',
            role: 'commercial',
            mot_de_passe: '',
            actif: true
        });
        setFormError('');
        setShowModal(true);
    };

    const handleEdit = (user) => {
        setModalMode('edit');
        setCurrentUser(user);
        setFormData({
            nom_utilisateur: user.nom_utilisateur,
            prenom_utilisateur: user.prenom_utilisateur,
            email: user.email,
            role: user.role,
            mot_de_passe: '',
            actif: user.actif === 1 || user.actif === true
        });
        setFormError('');
        setShowModal(true);
    };

    const handleDeleteClick = (user) => {
        setUserToDelete(user);
        setShowDeleteModal(true);
    };

    const handleConfirmDelete = async () => {
        if (!userToDelete) return;
        
        setIsDeleting(true);
        setError(null);
        
        try {
            const token = localStorage.getItem("token");
            const response = await fetch(
                `https://sawd.julesferry-ga.com/api/parametres.php?action=delete_utilisateur&id=${userToDelete.id_utilisateur}`,
                {
                    method: 'DELETE',
                    headers: { 'Authorization': `Bearer ${token}` }
                }
            );
            
            const result = await response.json();
            
            if (result.success) {
                setShowDeleteModal(false);
                setUserToDelete(null);
                setSuccess("✅ Utilisateur supprimé avec succès");
                setTimeout(() => setSuccess(''), 3000);
                fetchUtilisateurs();
            } else {
                setError(result.message || "Erreur lors de la suppression");
            }
        } catch (err) {
            setError("Erreur: " + err.message);
        } finally {
            setIsDeleting(false);
        }
    };

    const handleChange = (e) => {
        const { name, value, type, checked } = e.target;
        setFormData({
            ...formData,
            [name]: type === 'checkbox' ? checked : value
        });
    };

   // Dans votre handleSubmit du frontend, assurez-vous d'envoyer le mot de passe
const handleSubmit = async () => {
    // Validation
    if (!formData.nom_utilisateur || !formData.prenom_utilisateur || !formData.email) {
        setFormError("Veuillez remplir tous les champs obligatoires");
        return;
    }

    if (modalMode === 'add' && !formData.mot_de_passe) {
        setFormError("Le mot de passe est requis pour un nouvel utilisateur");
        return;
    }

    if (modalMode === 'add' && formData.mot_de_passe.length < 6) {
        setFormError("Le mot de passe doit contenir au moins 6 caractères");
        return;
    }

    setSaving(true);
    setFormError('');
    setError(null);
    
    try {
        const token = localStorage.getItem("token");
        
        let url = 'https://sawd.julesferry-ga.com/api/parametres.php';
        let method = 'POST';
        let body = {};
        
        if (modalMode === 'add') {
            // Création
            body = {
                action: 'create_utilisateur',
                ...formData
            };
        } else {
            // Modification
            url = `https://sawd.julesferry-ga.com/api/parametres.php?action=update_utilisateur&id=${currentUser.id_utilisateur}`;
            method = 'PUT';
            body = {
                nom_utilisateur: formData.nom_utilisateur,
                prenom_utilisateur: formData.prenom_utilisateur,
                email: formData.email,
                role: formData.role,
                actif: formData.actif
            };
        }
        
        const response = await fetch(url, {
            method: method,
            headers: {
                'Authorization': `Bearer ${token}`,
                'Content-Type': 'application/json'
            },
            body: JSON.stringify(body)
        });
        
        const result = await response.json();
        
        if (result.success) {
            setShowModal(false);
            setSuccess(modalMode === 'add' ? " Utilisateur créé avec succès" : " Utilisateur modifié avec succès");
            setTimeout(() => setSuccess(''), 3000);
            fetchUtilisateurs();
        } else {
            setFormError(result.message || "Erreur lors de l'opération");
        }
    } catch (err) {
        setFormError(err.message);
    } finally {
        setSaving(false);
    }
};

    const getRoleBadge = (role) => {
        const configs = {
            admin: { bg: 'danger', label: 'Administrateur', icon: 'ri-shield-user-line' },
            gestionnaire: { bg: 'warning', label: 'Gestionnaire', icon: 'ri-user-settings-line' },
            commercial: { bg: 'info', label: 'Commercial', icon: 'ri-user-star-line' },
            magasinier: { bg: 'secondary', label: 'Magasinier', icon: 'ri-user-line' }
        };
        const config = configs[role] || configs.commercial;
        return (
            <Badge 
                className="px-3 py-2 rounded-pill d-inline-flex align-items-center gap-1"
                style={{
                    background: `linear-gradient(135deg, var(--bs-${config.bg}), var(--bs-${config.bg}-dark))`,
                    border: 'none'
                }}
            >
                <i className={config.icon}></i>
                <span>{config.label}</span>
            </Badge>
        );
    };

    if (loading) {
        return (
            <>
                <Header />
                <div className="main main-app p-3 p-lg-4">
                    <div className="d-flex justify-content-center align-items-center" style={{ minHeight: '60vh' }}>
                        <div className="text-center">
                            <Spinner animation="border" variant="primary" className="mb-3" style={{ width: '3rem', height: '3rem' }} />
                            <p className="text-muted">Chargement des utilisateurs...</p>
                        </div>
                    </div>
                </div>
            </>
        );
    }

    return (
        <>
            <Header />
            <div className="main main-app p-3 p-lg-4">
                <div className="container-fluid">
                    {/* En-tête moderne */}
                    <Row className="mb-4 fade-in">
                        <Col md={12}>
                            <div className="d-flex justify-content-between align-items-center header-section">
                                <div className="d-flex align-items-center gap-3">
                                    <div 
                                        className="page-icon-wrapper d-flex align-items-center justify-content-center" 
                                        style={{ 
                                            width: '70px', 
                                            height: '70px', 
                                            borderRadius: '20px',
                                            background: 'linear-gradient(135deg, rgba(67, 97, 238, 0.15), rgba(58, 86, 212, 0.15))',
                                            boxShadow: '0 10px 20px rgba(67, 97, 238, 0.15)',
                                            display: 'flex',
                                            alignItems: 'center',
                                            justifyContent: 'center',
                                            flexShrink: 0
                                        }}
                                    >
                                        <i className="ri-user-settings-line text-primary" style={{ fontSize: '36px', lineHeight: 1 }}></i>
                                    </div>
                                    <div>
                                        <h1 className="page-title mb-1" style={{ fontSize: '2rem', fontWeight: 700, color: '#1e293b' }}>
                                            Utilisateurs
                                        </h1>
                                        <p className="page-subtitle mb-0" style={{ fontSize: '1rem', color: '#64748b' }}>
                                            Gérez les accès à l'application • {utilisateurs.length} utilisateur(s)
                                        </p>
                                    </div>
                                </div>
                                <div className="d-flex gap-2">
                                    <Button 
                                        variant="primary" 
                                        onClick={handleAdd}
                                        className="rounded-pill px-4 d-flex align-items-center gap-2"
                                        style={{
                                            background: 'linear-gradient(135deg, #4361ee, #3a56d4)',
                                            border: 'none'
                                        }}
                                    >
                                        <i className="ri-add-line"></i>
                                        <span>Nouvel utilisateur</span>
                                    </Button>
                                    <Button variant="outline-secondary" onClick={() => navigate('/parametres')} className="rounded-pill px-4">
                                        <i className="ri-arrow-left-line me-2"></i>Retour
                                    </Button>
                                </div>
                            </div>
                        </Col>
                    </Row>

                    {/* Messages */}
                    {success && (
                        <Alert 
                            variant="success" 
                            onClose={() => setSuccess('')} 
                            dismissible 
                            className="mb-4 alert-modern fade-in"
                            style={{
                                borderRadius: '12px',
                                borderLeft: '4px solid #10b981',
                                boxShadow: '0 4px 12px rgba(16, 185, 129, 0.2)'
                            }}
                        >
                            <div className="d-flex align-items-center">
                                <i className="ri-checkbox-circle-line fs-4 me-3 text-success"></i>
                                <div>
                                    <strong className="text-success">Succès</strong>
                                    <p className="mb-0">{success}</p>
                                </div>
                            </div>
                        </Alert>
                    )}
                    
                    {error && (
                        <Alert 
                            variant="danger" 
                            onClose={() => setError(null)} 
                            dismissible 
                            className="mb-4 alert-modern fade-in"
                            style={{
                                borderRadius: '12px',
                                borderLeft: '4px solid #dc3545',
                                boxShadow: '0 4px 12px rgba(220, 53, 69, 0.2)'
                            }}
                        >
                            <div className="d-flex align-items-center">
                                <i className="ri-error-warning-line fs-4 me-3 text-danger"></i>
                                <div>
                                    <strong className="text-danger">Erreur</strong>
                                    <p className="mb-0">{error}</p>
                                </div>
                            </div>
                        </Alert>
                    )}

                    {/* Tableau avec pagination */}
                    <Card className="border-0 shadow-sm table-card">
                        <Card.Header className="bg-white py-3 d-flex justify-content-between align-items-center">
                            <h5 className="mb-0">
                                <i className="ri-group-line me-2 text-primary"></i>
                                Liste des utilisateurs
                            </h5>
                            <Badge bg="primary" className="px-3 py-2 rounded-pill">
                                {utilisateurs.length} utilisateur(s)
                            </Badge>
                        </Card.Header>
                        <Card.Body className="p-0">
                            <div className="table-responsive">
                                <Table hover className="mb-0 align-middle custom-table">
                                    <thead className="bg-light">
                                        <tr>
                                            <th>Utilisateur</th>
                                            <th>Email</th>
                                            <th>Rôle</th>
                                            <th>Statut</th>
                                            <th>Dernière connexion</th>
                                            <th className="text-center">Actions</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {currentItems.length > 0 ? (
                                            currentItems.map((user, index) => (
                                                <tr key={user.id_utilisateur} style={{
                                                    animation: `fadeIn 0.3s ease ${index * 0.05}s both`
                                                }}>
                                                    <td>
                                                        <div className="d-flex align-items-center">
                                                            <div className="bg-light rounded-circle d-flex align-items-center justify-content-center me-2" 
                                                                 style={{ width: '40px', height: '40px' }}>
                                                                <i className="ri-user-3-line text-primary"></i>
                                                            </div>
                                                            <div>
                                                                <strong>{user.prenom_utilisateur} {user.nom_utilisateur}</strong>
                                                            </div>
                                                        </div>
                                                    </td>
                                                    <td>
                                                        <span className="d-flex align-items-center">
                                                            <i className="ri-mail-line me-2 text-muted"></i>
                                                            {user.email}
                                                        </span>
                                                    </td>
                                                    <td>{getRoleBadge(user.role)}</td>
                                                    <td>
                                                        {user.actif ? (
                                                            <Badge 
                                                                bg="success" 
                                                                className="rounded-pill px-3 py-2"
                                                                style={{
                                                                    background: 'linear-gradient(135deg, #10b981, #059669)',
                                                                    border: 'none'
                                                                }}
                                                            >
                                                                <i className="ri-check-line me-1"></i>Actif
                                                            </Badge>
                                                        ) : (
                                                            <Badge 
                                                                bg="secondary" 
                                                                className="rounded-pill px-3 py-2"
                                                                style={{
                                                                    background: 'linear-gradient(135deg, #6c757d, #5a6268)',
                                                                    border: 'none'
                                                                }}
                                                            >
                                                                <i className="ri-close-line me-1"></i>Inactif
                                                            </Badge>
                                                        )}
                                                    </td>
                                                    <td>
                                                        {user.derniere_connexion ? (
                                                            <span className="d-flex align-items-center">
                                                                <i className="ri-calendar-line me-2 text-muted"></i>
                                                                {formatDate(user.derniere_connexion)}
                                                            </span>
                                                        ) : (
                                                            <span className="text-muted">-</span>
                                                        )}
                                                    </td>
                                                    <td className="text-center">
                                                        <div className="d-flex justify-content-center gap-2">
                                                            <OverlayTrigger placement="top" overlay={<Tooltip>Modifier</Tooltip>}>
                                                                <Button 
                                                                    variant="outline-primary" 
                                                                    size="sm"
                                                                    className="rounded-circle"
                                                                    style={{ width: '36px', height: '36px' }}
                                                                    onClick={() => handleEdit(user)}
                                                                >
                                                                    <i className="ri-pencil-line"></i>
                                                                </Button>
                                                            </OverlayTrigger>
                                                            <OverlayTrigger placement="top" overlay={<Tooltip>Supprimer</Tooltip>}>
                                                                <Button 
                                                                    variant="outline-danger" 
                                                                    size="sm"
                                                                    className="rounded-circle"
                                                                    style={{ width: '36px', height: '36px' }}
                                                                    onClick={() => handleDeleteClick(user)}
                                                                >
                                                                    <i className="ri-delete-bin-line"></i>
                                                                </Button>
                                                            </OverlayTrigger>
                                                        </div>
                                                    </td>
                                                </tr>
                                            ))
                                        ) : (
                                            <tr>
                                                <td colSpan="6" className="text-center py-5">
                                                    <i className="ri-user-line fs-1 text-muted mb-3 d-block"></i>
                                                    <h5>Aucun utilisateur trouvé</h5>
                                                    <p className="text-muted">Commencez par créer un nouvel utilisateur</p>
                                                    <Button 
                                                        variant="primary" 
                                                        onClick={handleAdd}
                                                        className="mt-2"
                                                        style={{
                                                            background: 'linear-gradient(135deg, #4361ee, #3a56d4)',
                                                            border: 'none'
                                                        }}
                                                    >
                                                        <i className="ri-add-line me-2"></i>
                                                        Nouvel utilisateur
                                                    </Button>
                                                </td>
                                            </tr>
                                        )}
                                    </tbody>
                                </Table>
                            </div>

                            {/* Pagination */}
                            {totalPages > 1 && (
                                <div className="d-flex flex-column flex-md-row justify-content-between align-items-center p-3 border-top">
                                    <div className="text-muted small mb-2 mb-md-0">
                                        <i className="ri-file-list-line me-1"></i>
                                        Affichage de {indexOfFirstItem + 1} à {Math.min(indexOfLastItem, utilisateurs.length)} sur {utilisateurs.length} utilisateurs
                                    </div>
                                    <Pagination className="mb-0 flex-wrap justify-content-center">
                                        <Pagination.First 
                                            onClick={() => handlePageChange(1)}
                                            disabled={currentPage === 1}
                                        />
                                        <Pagination.Prev 
                                            onClick={() => handlePageChange(currentPage - 1)}
                                            disabled={currentPage === 1}
                                        />
                                        {renderPagination()}
                                        <Pagination.Next 
                                            onClick={() => handlePageChange(currentPage + 1)}
                                            disabled={currentPage === totalPages}
                                        />
                                        <Pagination.Last 
                                            onClick={() => handlePageChange(totalPages)}
                                            disabled={currentPage === totalPages}
                                        />
                                    </Pagination>
                                </div>
                            )}
                        </Card.Body>
                    </Card>
                </div>

                {/* Modal d'ajout/modification */}
                <Modal show={showModal} onHide={() => setShowModal(false)} centered className="user-modal">
                    <Modal.Header closeButton className="border-0 pb-0">
                        <Modal.Title className="w-100">
                            <div className="text-center">
                                <div className="confirm-icon mb-3">
                                    <div className={`icon-wrapper ${modalMode === 'add' ? 'bg-primary-soft' : 'bg-warning-soft'}`}>
                                        <i className={`${modalMode === 'add' ? 'ri-user-add-line text-primary' : 'ri-pencil-line text-warning'} fs-2`}></i>
                                    </div>
                                </div>
                                <h3 className="fw-bold text-primary">
                                    {modalMode === 'add' ? 'Nouvel utilisateur' : 'Modifier l\'utilisateur'}
                                </h3>
                            </div>
                        </Modal.Title>
                    </Modal.Header>
                    <Modal.Body className="pt-2">
                        {formError && (
                            <Alert variant="danger" className="mb-4">
                                <i className="ri-error-warning-line me-2"></i>
                                {formError}
                            </Alert>
                        )}
                        
                        <Form>
                            <Row>
                                <Col md={6}>
                                    <Form.Group className="mb-3">
                                        <Form.Label className="fw-semibold">Prénom</Form.Label>
                                        <Form.Control
                                            type="text"
                                            name="prenom_utilisateur"
                                            value={formData.prenom_utilisateur}
                                            onChange={handleChange}
                                            placeholder="Jean"
                                            required
                                        />
                                    </Form.Group>
                                </Col>
                                <Col md={6}>
                                    <Form.Group className="mb-3">
                                        <Form.Label className="fw-semibold">Nom</Form.Label>
                                        <Form.Control
                                            type="text"
                                            name="nom_utilisateur"
                                            value={formData.nom_utilisateur}
                                            onChange={handleChange}
                                            placeholder="Dupont"
                                            required
                                        />
                                    </Form.Group>
                                </Col>
                            </Row>
                            
                            <Form.Group className="mb-3">
                                <Form.Label className="fw-semibold">Email</Form.Label>
                                <InputGroup>
                                    <InputGroup.Text>
                                        <i className="ri-mail-line"></i>
                                    </InputGroup.Text>
                                    <Form.Control
                                        type="email"
                                        name="email"
                                        value={formData.email}
                                        onChange={handleChange}
                                        placeholder="jean.dupont@exemple.com"
                                        required
                                    />
                                </InputGroup>
                            </Form.Group>
                            
                            {modalMode === 'add' && (
                                <Form.Group className="mb-3">
                                    <Form.Label className="fw-semibold">Mot de passe</Form.Label>
                                    <InputGroup>
                                        <InputGroup.Text>
                                            <i className="ri-lock-line"></i>
                                        </InputGroup.Text>
                                        <Form.Control
                                            type="password"
                                            name="mot_de_passe"
                                            value={formData.mot_de_passe}
                                            onChange={handleChange}
                                            placeholder="••••••••"
                                            required={modalMode === 'add'}
                                        />
                                    </InputGroup>
                                </Form.Group>
                            )}
                            
                            <Form.Group className="mb-3">
                                <Form.Label className="fw-semibold">Rôle</Form.Label>
                                <Form.Select name="role" value={formData.role} onChange={handleChange}>
                                    <option value="admin">Administrateur</option>
                                    <option value="gestionnaire">Gestionnaire</option>
                                    <option value="commercial">Commercial</option>
                                    <option value="magasinier">Magasinier</option>
                                </Form.Select>
                            </Form.Group>
                            
                            <Form.Group className="mb-3">
                                <Form.Check
                                    type="switch"
                                    id="actif"
                                    name="actif"
                                    label="Compte actif"
                                    checked={formData.actif}
                                    onChange={handleChange}
                                />
                            </Form.Group>
                        </Form>
                    </Modal.Body>
                    <Modal.Footer className="border-0 pt-0 justify-content-center">
                        <Button 
                            variant="outline-secondary" 
                            onClick={() => setShowModal(false)}
                            className="px-4 rounded-pill"
                        >
                            <i className="ri-close-line me-2"></i>Annuler
                        </Button>
                        <Button 
                            variant="primary" 
                            onClick={handleSubmit} 
                            disabled={saving}
                            className="px-5 rounded-pill"
                            style={{
                                background: 'linear-gradient(135deg, #4361ee, #3a56d4)',
                                border: 'none'
                            }}
                        >
                            {saving ? (
                                <>
                                    <Spinner size="sm" className="me-2" />
                                    Traitement...
                                </>
                            ) : (
                                <>
                                    <i className="ri-check-line me-2"></i>
                                    {modalMode === 'add' ? 'Créer' : 'Modifier'}
                                </>
                            )}
                        </Button>
                    </Modal.Footer>
                </Modal>

                {/* Modal de suppression élégant */}
                <Modal show={showDeleteModal} onHide={() => setShowDeleteModal(false)} centered className="delete-modal">
                    <Modal.Header closeButton className="border-0 pb-0">
                        <Modal.Title className="w-100">
                            <div className="text-center">
                                <div className="confirm-icon mb-3">
                                    <div className="icon-wrapper bg-danger-soft">
                                        <i className="ri-delete-bin-line text-danger" style={{ fontSize: '3rem' }}></i>
                                    </div>
                                </div>
                                <h3 className="fw-bold text-danger">Supprimer l'utilisateur</h3>
                            </div>
                        </Modal.Title>
                    </Modal.Header>
                    
                    <Modal.Body className="text-center pt-2">
                        <p className="text-muted mb-4">
                            Êtes-vous sûr de vouloir supprimer <strong>{userToDelete?.prenom_utilisateur} {userToDelete?.nom_utilisateur}</strong> ?
                        </p>
                        
                        <Alert variant="warning" className="text-start mb-4">
                            <div className="d-flex">
                                <i className="ri-alert-line fs-4 me-3 flex-shrink-0"></i>
                                <div>
                                    <strong>Cette action est irréversible</strong>
                                    <p className="mb-0 small mt-1">
                                        La suppression entraînera la perte définitive de toutes les informations 
                                        liées à cet utilisateur. Les données historiques (créations, modifications) 
                                        seront conservées mais l'utilisateur ne pourra plus se connecter.
                                    </p>
                                </div>
                            </div>
                        </Alert>

                        <div className="bg-light p-3 rounded-3 mb-3">
                            <div className="d-flex justify-content-between mb-2">
                                <span className="text-muted">Nom complet :</span>
                                <span className="fw-semibold">{userToDelete?.prenom_utilisateur} {userToDelete?.nom_utilisateur}</span>
                            </div>
                            <div className="d-flex justify-content-between mb-2">
                                <span className="text-muted">Email :</span>
                                <span className="fw-semibold">{userToDelete?.email}</span>
                            </div>
                            <div className="d-flex justify-content-between">
                                <span className="text-muted">Rôle :</span>
                                <span className="fw-semibold">{getRoleBadge(userToDelete?.role)}</span>
                            </div>
                        </div>
                    </Modal.Body>
                    
                    <Modal.Footer className="border-0 pt-0 justify-content-center">
                        <Button 
                            variant="outline-secondary" 
                            onClick={() => setShowDeleteModal(false)}
                            className="px-4 rounded-pill"
                            disabled={isDeleting}
                        >
                            <i className="ri-close-line me-2"></i>Annuler
                        </Button>
                        <Button 
                            variant="danger" 
                            onClick={handleConfirmDelete}
                            className="px-5 rounded-pill"
                            disabled={isDeleting}
                            style={{
                                background: 'linear-gradient(135deg, #dc3545, #bb2d3b)',
                                border: 'none'
                            }}
                        >
                            {isDeleting ? (
                                <>
                                    <Spinner size="sm" className="me-2" />
                                    Suppression...
                                </>
                            ) : (
                                <>
                                    <i className="ri-delete-bin-line me-2"></i>
                                    Supprimer
                                </>
                            )}
                        </Button>
                    </Modal.Footer>
                </Modal>

                <style>{`
                    @keyframes fadeIn {
                        from { opacity: 0; transform: translateY(10px); }
                        to { opacity: 1; transform: translateY(0); }
                    }

                    .fade-in {
                        animation: fadeIn 0.5s ease;
                    }

                    .alert-modern {
                        animation: slideInDown 0.3s ease-out;
                        border: none;
                        padding: 1rem 1.5rem;
                    }

                    @keyframes slideInDown {
                        from { transform: translateY(-20px); opacity: 0; }
                        to { transform: translateY(0); opacity: 1; }
                    }

                    .table-card {
                        border-radius: 16px;
                        overflow: hidden;
                    }

                    .custom-table thead th {
                        background: #f8fafc;
                        color: #1e293b;
                        font-weight: 600;
                        font-size: 0.85rem;
                        text-transform: uppercase;
                        letter-spacing: 0.5px;
                        border-bottom: 2px solid #e2e8f0;
                        padding: 1rem 0.75rem;
                    }

                    .custom-table tbody td {
                        padding: 1rem 0.75rem;
                        vertical-align: middle;
                    }

                    .custom-table tbody tr {
                        transition: all 0.2s ease;
                    }

                    .custom-table tbody tr:hover {
                        background: #f1f5f9 !important;
                    }

                    /* Styles des modales */
                    .user-modal .modal-content,
                    .delete-modal .modal-content {
                        border: none;
                        border-radius: 24px;
                        box-shadow: 0 20px 40px rgba(0,0,0,0.2);
                        overflow: hidden;
                    }

                    .user-modal .modal-header,
                    .delete-modal .modal-header {
                        background: linear-gradient(135deg, #f8fafc, #ffffff);
                        border-bottom: none;
                        padding: 2rem 1.5rem 0.5rem;
                    }

                    .user-modal .modal-body,
                    .delete-modal .modal-body {
                        padding: 1rem 2rem;
                    }

                    .user-modal .modal-footer,
                    .delete-modal .modal-footer {
                        border-top: 1px solid #e2e8f0;
                        padding: 1.5rem 2rem 2rem;
                        background: #f8fafc;
                    }

                    .icon-wrapper {
                        width: 80px;
                        height: 80px;
                        margin: 0 auto;
                        display: flex;
                        align-items: center;
                        justify-content: center;
                        border-radius: 50%;
                        animation: pulse 2s infinite;
                    }

                    .bg-primary-soft {
                        background: rgba(67, 97, 238, 0.15);
                    }

                    .bg-warning-soft {
                        background: rgba(245, 158, 11, 0.15);
                    }

                    .bg-danger-soft {
                        background: rgba(220, 53, 69, 0.15);
                    }

                    .confirm-icon {
                        animation: bounceIn 0.5s ease-out;
                    }

                    @keyframes bounceIn {
                        0% { transform: scale(0); opacity: 0; }
                        50% { transform: scale(1.1); }
                        100% { transform: scale(1); opacity: 1; }
                    }

                    @keyframes pulse {
                        0% { transform: scale(1); }
                        50% { transform: scale(1.05); }
                        100% { transform: scale(1); }
                    }

                    .user-modal .btn,
                    .delete-modal .btn {
                        border-radius: 12px;
                        font-weight: 600;
                        padding: 0.6rem 1.5rem;
                        transition: all 0.3s ease;
                    }

                    .user-modal .btn:hover:not(:disabled),
                    .delete-modal .btn:hover:not(:disabled) {
                        transform: translateY(-2px);
                        box-shadow: 0 8px 16px rgba(0,0,0,0.1);
                    }

                    .delete-modal .alert {
                        border: none;
                        border-radius: 12px;
                        background: linear-gradient(135deg, #fff3cd, #fff9e6);
                        border-left: 4px solid #ffc107;
                    }

                    /* Styles pour la pagination */
                    .pagination {
                        gap: 5px;
                    }
                    
                    .pagination .page-item .page-link {
                        border-radius: 8px;
                        border: 1px solid #e2e8f0;
                        color: #1e293b;
                        padding: 0.5rem 1rem;
                        transition: all 0.2s ease;
                        font-weight: 500;
                    }
                    
                    .pagination .page-item.active .page-link {
                        background: linear-gradient(135deg, #4361ee, #3a56d4);
                        border-color: #4361ee;
                        color: white;
                        box-shadow: 0 4px 10px rgba(67, 97, 238, 0.3);
                    }
                    
                    .pagination .page-item .page-link:hover {
                        background: #f1f5f9;
                        transform: translateY(-2px);
                        box-shadow: 0 4px 8px rgba(0,0,0,0.1);
                    }
                    
                    .pagination .page-item.disabled .page-link {
                        opacity: 0.5;
                        pointer-events: none;
                    }
                `}</style>

                <Footer />
            </div>
        </>
    );
}